<template>
    <div class="glass-container">
        <div class="vendors-management-container">
            <!-- Main Card -->
            <div class="modern-card">
                <!-- Header Section -->
                <div class="header-section">
                    <div class="title-section">
                        <h1 class="page-title">Vendors Management</h1>
                        <p class="page-subtitle">Manage vendor information and details</p>
                    </div>
                    
                    <div class="action-buttons">
                        <b-button @click="add_new" variant="primary" class="action-btn add-btn">
                            <b-icon icon="plus-circle" class="mr-2"></b-icon>
                            Add Vendor
                        </b-button>
                        <b-button @click="fetch_init" variant="outline-secondary" class="action-btn refresh-btn">
                            <b-icon icon="arrow-clockwise" class="mr-2"></b-icon>
                            Refresh
                        </b-button>
                    </div>
                </div>

                <!-- Info Alert -->
                <b-alert variant="info" show class="info-alert">
                    <div class="alert-content">
                        <b-icon icon="info-circle" class="mr-2"></b-icon>
                        <div>
                            <strong>Important:</strong> This tool contains list of vendors in the system. 
                            You can add new vendors here. If you change a name or description of any existing vendor, 
                            please click <strong>SAVE</strong> button once done changing the entry.
                        </div>
                    </div>
                </b-alert>

                <!-- Loading State -->
                <div v-if="items_being_loaded" class="loading-container">
                    <loading 
                        :active="items_being_loaded"
                        :can-cancel="false"
                        :is-full-page="false"
                    ></loading>
                </div>

                <!-- Vendors List -->
                <div v-else class="vendors-list-container">
                    <div v-if="items.length > 0">
                        <div v-for="(item, idx) in items" :key="idx" class="vendor-card">
                            <!-- Vendor Header -->
                            <div class="vendor-header">
                                <div class="vendor-name">
                                    <span class="field-label">VENDOR NAME</span>
                                    <b-input 
                                        v-model="item.vendor_name" 
                                        size="sm" 
                                        class="name-input"
                                        placeholder="Enter vendor name"
                                        :class="{ 'new-vendor': !item.vendor_id }"
                                    ></b-input>
                                </div>
                                
                                <div class="vendor-account">
                                    <span class="field-label">ACCOUNT NUMBER</span>
                                    <b-input 
                                        v-model="item.account_number" 
                                        size="sm" 
                                        class="account-input"
                                        placeholder="Account number"
                                    ></b-input>
                                </div>
                                
                                <div class="vendor-description">
                                    <span class="field-label">DESCRIPTION</span>
                                    <b-input 
                                        v-model="item.vendor_description" 
                                        size="sm" 
                                        class="description-input"
                                        placeholder="Vendor description"
                                    ></b-input>
                                </div>
                            </div>

                            <!-- Contact Information Row -->
                            <div class="contact-row">
                                <!-- Email -->
                                <div class="contact-field">
                                    <span class="field-label">EMAIL</span>
                                    <b-input 
                                        v-model="item.email" 
                                        size="sm" 
                                        class="email-input"
                                        placeholder="vendor@example.com"
                                    ></b-input>
                                </div>
                                
                                <!-- Phone -->
                                <div class="contact-field">
                                    <span class="field-label">PHONE</span>
                                    <b-input 
                                        v-model="item.phone" 
                                        size="sm" 
                                        class="phone-input"
                                        placeholder="Phone number"
                                    ></b-input>
                                </div>
                                
                                <!-- Address -->
                                <div class="contact-field address-field">
                                    <span class="field-label">ADDRESS</span>
                                    <b-input 
                                        v-model="item.address" 
                                        size="sm" 
                                        class="address-input"
                                        placeholder="Vendor address"
                                    ></b-input>
                                </div>
                            </div>

                            <!-- Action Buttons -->
                            <div class="vendor-actions">
                                <div class="vendor-status" v-if="item.vendor_id">
                                    <span class="vendor-id">ID: {{ item.vendor_id }}</span>
                                    <span class="vendor-added">Added: {{ formatDate(item.ts) }}</span>
                                </div>
                                
                                <div class="action-buttons-wrapper">
                                    <b-button 
                                        @click="save_vendor(item)" 
                                        variant="success" 
                                        size="sm"
                                        class="action-btn save-btn"
                                        :disabled="!item.vendor_name"
                                    >
                                        <b-icon icon="check" class="mr-1"></b-icon>
                                        Save
                                    </b-button>
                                    
                                    <b-button 
                                        @click="remove_vendor(idx)" 
                                        variant="outline-danger" 
                                        size="sm"
                                        class="action-btn cancel-btn"
                                        v-if="!item.vendor_id"
                                    >
                                        <b-icon icon="x" class="mr-1"></b-icon>
                                        Cancel
                                    </b-button>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Table Footer -->
                        <div class="table-footer">
                            <div class="pagination-info">
                                Showing {{ items.length }} vendors
                            </div>
                        </div>
                    </div>
                    
                    <!-- Empty State -->
                    <div v-else class="empty-state">
                        <div class="empty-state-icon">
                            <b-icon icon="shop" font-scale="3"></b-icon>
                        </div>
                        <h3>No Vendors Found</h3>
                        <p>No vendors in the system yet</p>
                        <div class="empty-state-actions mt-3">
                            <b-button @click="add_new" variant="primary">
                                <b-icon icon="plus-circle" class="mr-2"></b-icon>
                                Add First Vendor
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Requestor from '@/utilities/Requestor'
    import Loading from 'vue-loading-overlay';
    import 'vue-loading-overlay/dist/vue-loading.css';

    export default {
        name: "ListVendors",
        components: { Loading },
        data: function() {
            return {
                packet: {},
                items: [],
                items_being_loaded: false,
            }
        },
        methods: {
            remove_vendor: function(idx){
                this.items.splice(idx, 1);
            },
            save_vendor: function(item){
                let path = "/api/vendor/save_vendor/"
                let data = item;
                let that = this;
                let s = function(response){
                    if (response.data.error){
                        that.$bvToast.toast(
                            response.data.error, 
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    }
                    else {
                        that.$bvToast.toast(
                            "Vendor saved successfully.", 
                            {title: "Success", autoHideDelay: 1500, variant:"success"}
                        );
                        item.vendor_id = response.data.result[0];
                        that.fetch_init();
                    }
                    that.items_being_loaded = false;
                }
                let f = function(error){
                    console.log(error);
                    that.$bvToast.toast(
                        "Error saving vendor. Please try again.", 
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                    that.items_being_loaded = false;
                }
                this.items_being_loaded = true
                Requestor.postSyncP(path, data, this, s, f);
            },
            
            fetch_init: function(){
                let path = "/api/vendor/get_all_vendors/"
                let data = {}
                let that = this;
                let s = function(response){
                    if (response.data.error){
                        that.$bvToast.toast(
                            response.data.error, 
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    }
                    else {
                        that.items = response.data.result;
                        that.items.forEach(item => {
                            if (!item.account_number) item.account_number = '';
                            if (!item.email) item.email = '';
                            if (!item.phone) item.phone = '';
                            if (!item.address) item.address = '';
                        });
                    }
                    that.items_being_loaded = false;
                }
                let f = function(error){
                    console.log(error);
                    that.$bvToast.toast(
                        "Error loading vendors list. Please refresh.", 
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                    that.items_being_loaded = false;
                }
                this.items_being_loaded = true
                Requestor.getSyncP(path, data, this, s, f);
            },
            add_new: function(){
                let item = {
                    vendor_id: null,
                    vendor_name: null,
                    vendor_description: null,
                    account_number: null,
                    email: null,
                    phone: null,
                    address: null,
                    ts: null,
                    edit: false,
                }
                this.items.unshift(item);
            },
            formatDate: function(dateString) {
                if (!dateString) return 'N/A';
                try {
                    const date = new Date(dateString);
                    return date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                    });
                } catch (error) {
                    return dateString;
                }
            }
        },
        created: function(){
            this.packet = JSON.parse(localStorage.packet);
            this.packet.userDetails = JSON.parse(localStorage.userDetails);
            this.fetch_init();
        }
    }
</script>

<style scoped>
.glass-container {
    min-height: 100vh;
    padding: 20px;
    background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
    background-size: cover;
    background-position: center;
    background-attachment: fixed;
    position: relative;
    overflow: hidden;
}

.glass-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(2px);
    z-index: 0;
}

.vendors-management-container {
    padding: 20px;
    background: transparent;
    min-height: 100vh;
    border-radius: 34px;
    z-index: 1;
    position: relative;
}

.modern-card {
    background: rgba(255, 255, 255, 0.7);
    border-radius: 15px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    padding: 25px;
    margin: 0 auto;
    margin-top: 10px;
    max-width: 1800px;
}

/* Header */
.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 25px;
    padding-bottom: 20px;
    border-bottom: 2px solid #f0f0f0;
}

.title-section .page-title {
    font-size: 1.8rem;
    font-weight: 700;
    color: #2d3748;
    margin: 0;
}

.title-section .page-subtitle {
    color: #718096;
    font-size: 0.95rem;
    margin: 5px 0 0;
}

.action-buttons {
    display: flex;
    gap: 10px;
}

.action-btn {
    padding: 8px 16px;
    font-weight: 600;
    border-radius: 8px;
    transition: all 0.2s ease;
}

.add-btn {
    background: #667eea;
    border: none;
}

.add-btn:hover {
    background: #5a67d8;
    transform: translateY(-1px);
}

.refresh-btn:hover {
    transform: translateY(-1px);
}

/* Info Alert */
.info-alert {
    background: rgba(13, 110, 253, 0.1);
    border: 1px solid rgba(13, 110, 253, 0.2);
    border-radius: 8px;
    margin-bottom: 25px;
}

.alert-content {
    display: flex;
    align-items: flex-start;
}

.alert-content .bi {
    color: #0d6efd;
    margin-top: 2px;
}

/* Loading */
.loading-container {
    text-align: center;
    padding: 40px 20px;
}

/* Vendor Card */
.vendor-card {
    background: #ffffff;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    padding: 20px;
    margin-bottom: 15px;
    border: 1px solid #e2e8f0;
    transition: all 0.3s ease;
}

.vendor-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #cbd5e0;
}

/* Vendor Header */
.vendor-header {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    margin-bottom: 20px;
    padding-bottom: 20px;
    border-bottom: 1px solid #e2e8f0;
}

.vendor-name {
    flex: 2;
    min-width: 250px;
}

.vendor-account {
    flex: 1;
    min-width: 200px;
}

.vendor-description {
    flex: 2;
    min-width: 300px;
}

/* Field Label */
.field-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: #718096;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
}

/* Input Styles */
.name-input {
    background: #f8f9fa;
    border-color: #e2e8f0;
    font-size: 12px;
    font-weight: bold;
}

.name-input.new-vendor {
    background: #fff3cd;
    border-color: #ffeaa7;
}

.name-input:focus, 
.account-input:focus, 
.description-input:focus,
.email-input:focus, 
.phone-input:focus, 
.address-input:focus {
    background: #ffffff;
    border-color: #667eea;
    box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.account-input,
.description-input,
.email-input,
.phone-input,
.address-input {
    background: #f8f9fa;
    border-color: #e2e8f0;
    font-size: 12px;
}

/* Contact Row */
.contact-row {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    margin-bottom: 20px;
}

.contact-field {
    flex: 1;
    min-width: 200px;
}

.contact-field.address-field {
    flex: 2;
    min-width: 300px;
}

/* Vendor Actions */
.vendor-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 15px;
    border-top: 1px solid #e2e8f0;
}

.vendor-status {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.vendor-id {
    font-size: 12px;
    color: #718096;
    font-weight: 500;
    background: #f8f9fa;
    padding: 3px 8px;
    border-radius: 4px;
    display: inline-block;
}

.vendor-added {
    font-size: 11px;
    color: #a0aec0;
}

.action-buttons-wrapper {
    display: flex;
    gap: 10px;
}

.save-btn {
    background: #48bb78;
    border-color: #48bb78;
    color: white;
    padding: 6px 12px;
    font-size: 12px;
}

.save-btn:hover {
    background: #38a169;
    border-color: #38a169;
}

.save-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.cancel-btn {
    color: #f56565;
    border-color: #f56565;
    padding: 6px 12px;
    font-size: 12px;
}

.cancel-btn:hover {
    background: #f56565;
    color: white;
}

/* Table Footer */
.table-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 0;
    margin-top: 15px;
    border-top: 1px solid #e2e8f0;
}

.pagination-info {
    color: #718096;
    font-size: 0.85rem;
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 40px 20px;
    border-radius: 8px;
    background: #f8fafc;
    border: 1px dashed #e2e8f0;
}

.empty-state-icon {
    color: #a0aec0;
    margin-bottom: 15px;
}

.empty-state h3 {
    color: #2d3748;
    margin-bottom: 8px;
    font-weight: 600;
}

.empty-state p {
    color: #718096;
    margin-bottom: 15px;
}

.empty-state-actions {
    display: flex;
    gap: 10px;
    justify-content: center;
    flex-wrap: wrap;
}

/* Responsive Design */
@media (max-width: 1200px) {
    .vendor-header {
        gap: 15px;
    }
    
    .vendor-name,
    .vendor-account,
    .vendor-description {
        min-width: calc(50% - 15px);
    }
    
    .contact-field {
        min-width: calc(50% - 10px);
    }
    
    .contact-field.address-field {
        min-width: 100%;
    }
}

@media (max-width: 768px) {
    .modern-card {
        padding: 15px;
    }
    
    .header-section {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
    }
    
    .action-buttons {
        width: 100%;
    }
    
    .action-btn {
        flex: 1;
        justify-content: center;
    }
    
    .vendor-header {
        flex-direction: column;
        gap: 15px;
    }
    
    .vendor-name,
    .vendor-account,
    .vendor-description {
        width: 100%;
        min-width: unset;
    }
    
    .contact-row {
        flex-direction: column;
        gap: 15px;
    }
    
    .contact-field,
    .contact-field.address-field {
        width: 100%;
        min-width: unset;
    }
    
    .vendor-actions {
        flex-direction: column;
        gap: 15px;
        align-items: stretch;
    }
    
    .action-buttons-wrapper {
        justify-content: flex-end;
    }
}

@media (max-width: 480px) {
    .action-buttons {
        flex-direction: column;
    }
    
    .action-btn {
        width: 100%;
        justify-content: center;
    }
    
    .vendor-actions {
        align-items: stretch;
    }
    
    .action-buttons-wrapper {
        flex-direction: column;
    }
    
    .save-btn,
    .cancel-btn {
        width: 100%;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/ControlPanel/ListVendors.vue